import type { ReactNode } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  EntitlementDeniedState,
  ErrorState,
  LoadingState,
} from "@odingard/design-system";
import { ApiError } from "@odingard/sdk-client";
import type { AsyncState } from "../lib/useAsync";
import { describeDenial, missingPermissionFrom } from "./permissions";

/**
 * Inline surfacing of a backend refusal, keeping the code and the named key.
 * Governance screens must never rewrite a denial into a generic error — the key
 * is what an administrator needs in order to grant access.
 */
export function GovernanceDenial({ error }: { error: ApiError }) {
  const permission = missingPermissionFrom(error);
  const entitlement =
    typeof error.details?.entitlement === "string" ? error.details.entitlement : null;
  return (
    <div className="ods-denial" role="alert">
      <p style={{ margin: 0 }}>{describeDenial(error)}</p>
      <p style={{ margin: "6px 0 0" }}>
        <code>{error.code}</code>
        {permission ? (
          <>
            {" "}
            · missing permission <code>{permission}</code>
          </>
        ) : null}
        {entitlement ? (
          <>
            {" "}
            · requires entitlement <code>{entitlement}</code>
          </>
        ) : null}
      </p>
    </div>
  );
}

export interface GovPanelProps<T> {
  title?: ReactNode;
  action?: ReactNode;
  state: AsyncState<T>;
  /** Treated as empty when this returns true for the loaded value. */
  isEmpty?: (data: T) => boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  loadingLabel?: string;
  children: (data: T) => ReactNode;
  /** Renders without the surrounding card, for full-width sections. */
  bare?: boolean;
}

/**
 * The loading / denied / unavailable / error / empty ladder every governance
 * screen repeats. Keeping it here means a new family cannot accidentally ship
 * a screen that renders nothing, or one that swallows a permission denial.
 */
export function GovPanel<T>({
  title,
  action,
  state,
  isEmpty,
  emptyTitle = "Nothing here yet",
  emptyDescription,
  emptyAction,
  loadingLabel = "Loading…",
  children,
  bare = false,
}: GovPanelProps<T>) {
  const body = (() => {
    if (state.loading) return <LoadingState label={loadingLabel} />;
    if (state.error) {
      if (state.error.status === 403) return <GovernanceDenial error={state.error} />;
      if (state.error.code === "entitlement_denied") {
        return <EntitlementDeniedState product="Governance & Compliance" />;
      }
      if (state.error.code === "service_unavailable") {
        return (
          <ErrorState
            title="Governance is temporarily unavailable"
            description="The Policy AI service is not reachable right now. No governance data is being shown."
            onRetry={state.reload}
          />
        );
      }
      return <ErrorState description={state.error.message} onRetry={state.reload} />;
    }
    if (state.data === null) return <LoadingState label={loadingLabel} />;
    if (isEmpty?.(state.data)) {
      return (
        <EmptyState
          title={emptyTitle}
          {...(emptyDescription ? { description: emptyDescription } : {})}
          action={emptyAction}
        />
      );
    }
    return <>{children(state.data)}</>;
  })();

  if (bare) return body;
  return (
    <Card>
      {title ? <CardHeader title={title} action={action} /> : null}
      <CardBody>{body}</CardBody>
    </Card>
  );
}
